import { Box } from '@mui/material';
import { daysLeft } from '@timelog/pm-shared';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { i18n } from '@/i18n';
import { todayIso } from '@/lib';

/**
 * CALC-6 buckets → the design's colors: overdue coral, ≤3 days amber.
 *
 * The amber is `--fg-warning-text`, the one PM-local colour with no design
 * system counterpart — see the note on it in tokens.css.
 */
const BUCKET_COLORS = {
  none: 'var(--fg-3)',
  over: 'var(--color-coral-100)',
  'due-today': 'var(--fg-warning-text)',
  soon: 'var(--fg-warning-text)',
  later: 'var(--fg-3)',
} as const;

/**
 * `t` is optional so existing callers keep working; without it the label is in
 * the current UI language, but a caller that wants to re-render on a language
 * switch should pass its own `useTranslation('pmPlanning')` t.
 */
export const daysLeftDisplay = (
  targetDate: string | undefined,
  t: TFunction<'pmPlanning'> = i18n.getFixedT(null, 'pmPlanning'),
): { label: string; color: string } => {
  const info = daysLeft(targetDate, todayIso());
  const color = BUCKET_COLORS[info.bucket];
  switch (info.bucket) {
    case 'none':
      return { label: t('daysLeft.NoTarget'), color };
    case 'over': {
      const days = Math.abs(info.days ?? 0);
      return { label: t('daysLeft.DaysOver', { count: days }), color };
    }
    case 'due-today':
      return { label: t('daysLeft.DueToday'), color };
    default: {
      const days = info.days ?? 0;
      return { label: t('daysLeft.DaysLeft', { count: days }), color };
    }
  }
};

export type DaysLeftLabelProps = {
  targetDate: string | undefined;
  fontSize?: number;
};

export const DaysLeftLabel = ({ targetDate, fontSize = 10 }: DaysLeftLabelProps) => {
  const { t } = useTranslation('pmPlanning');
  const { label, color } = daysLeftDisplay(targetDate, t);
  return (
    <Box component="span" sx={{ fontSize, color }} data-automation-id="days-left">
      {label}
    </Box>
  );
};
